<template>
  <div class="aiw2-publish">
    <!-- Main column: hero + status + article preview (the side panel sits beside it) -->
    <div class="publish-main">
    <hero title="Publish" :keyword="keyword" :mode="mode" :page-run="pageRun" :ai-run-id="aiRunId" :meta-items="metaItems" hide-mode report-details
      info-title="Your article is finished. Preview it here, then download it in the format you need. POP doesn't publish to your site, you add it yourself." />

      <!-- Article preview -->
      <div class="article-preview" :class="{ 'mode-html': tab === 'HTML' }">
        <div class="article-preview-toolbar">
          <div class="dots"><span></span><span></span><span></span></div>
          <div class="url-bar"><span class="lock">🔒</span>{{ previewUrl }}</div>
          <div class="preview-tabs">
            <button v-for="t in TABS" :key="t" class="preview-tab-btn" :class="{ active: tab === t }" @click="tab = t">{{ t }}</button>
          </div>
        </div>

        <div v-if="tab !== 'HTML'" class="preview-controls-bar">
          <label class="kw-toggle" @click="highlight = !highlight">
            <span class="sw" :class="{ on: highlight }"><span class="knob"></span></span>
            Highlight keywords
          </label>
          <button class="pub-btn btn-soft btn-sm" @click="startOver"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg> Start over</button>
        </div>

        <div v-if="loading" class="loading"><span class="spin"></span> Loading preview…</div>
        <template v-else>
          <div v-if="tab === 'HTML'" class="article-html-wrap">
            <button class="html-copy-btn" @click="copyHtml">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>
              Copy HTML
            </button>
            <pre class="article-html-view">{{ beautified }}</pre>
          </div>
          <article v-else class="article-body" :class="{ mobile: tab === 'Mobile' }" @mouseover="onArtMouseOver">
            <h1 v-if="articleTitle" class="art-h1">{{ articleTitle }}</h1>
            <div class="art-content" v-html="renderedBody"></div>
            <!-- Floating per-image download (positioned over the hovered image;
                 lives OUTSIDE .art-content so exported/copied HTML is untouched). -->
            <button
              v-show="imgDl.show"
              class="img-dl-btn"
              :style="{ top: imgDl.top + 'px', left: imgDl.left + 'px' }"
              title="Download this image"
              @click.stop="downloadHoverImage"
            ><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg></button>
          </article>
        </template>
      </div>
    </div>

    <!-- Publish side panel (right column) -->
    <aside class="publish-side">
        <!-- Own-key run: which key/model this article billed -->
        <div v-if="ownKeyProvider" class="publish-side-card api-key-card" :class="'prov-' + ownKeyProvider">
          <div class="akc-ico"><span class="akc-mark">{{ ownKeyProvider === 'anthropic' ? 'CL' : 'AI' }}</span></div>
          <div class="akc-body">
            <div class="akc-title">{{ ownKeyProvider === 'anthropic' ? 'Claude' : 'OpenAI' }} key is on</div>
            <div class="akc-sub">This article bills {{ ownKeyProvider === 'anthropic' ? 'Claude' : 'GPT' }} usage to your {{ ownKeyProvider === 'anthropic' ? 'Anthropic' : 'OpenAI' }} account.</div>
            <span v-if="ownKeyModelLabel" class="akc-model">{{ ownKeyModelLabel }}</span>
          </div>
        </div>
        <!-- Regenerate credits history -->
        <div class="publish-side-card">
          <div class="side-card-head" v-click-outside="() => { rcMenuOpen = false; }">
            <h4><span class="rc-icon"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg></span> Regenerate credits history</h4>
            <button class="more" aria-label="Regenerate run breakdown" @click="rcMenuOpen = !rcMenuOpen">⋯</button>
            <div class="rc-menu" :class="{ open: rcMenuOpen }">
              <div class="rc-menu-row"><span>Regenerate run - Search engine title and page title</span><b>{{ counts.title }}</b></div>
              <div class="rc-menu-row"><span>Regenerate run - Sub-headings (H2 and H3)</span><b>{{ counts.heads }}</b></div>
              <div class="rc-menu-row"><span>Regenerate run - Main content</span><b>{{ counts.content }}</b></div>
              <div class="rc-menu-row"><span>Regenerate run - Rich content</span><b>{{ counts.rich }}</b></div>
            </div>
          </div>
          <a class="credits-link" @click="showRegenExplain = !showRegenExplain">{{ showRegenExplain ? '▾' : '▸' }} Regeneration Credits Explained</a>
          <p v-if="showRegenExplain" class="credits-explain">Each article includes <strong>5 free regenerate credits</strong> (Title / H1, sub-headings, main content). <template v-if="ownKeyFree">After that, regenerates stay <strong>free</strong> because this article uses your own API key.</template><template v-else>After all 5 are used, each regenerate run costs <strong>1 credit</strong>.</template></p>
          <template v-if="unlimitedRegen">
            <div class="credits-row"><span>Regenerate credits</span><span class="val unlimited">Unlimited</span></div>
          </template>
          <template v-else>
            <div class="credits-row"><span>Used free regenerate credits</span><span class="val" :class="{ over: paid > 0 }">{{ freeUsed }} / 5{{ paid > 0 ? ' · ' + paid + ' paid' : '' }}</span></div>
            <div class="credits-bar"><div :class="{ over: paid > 0 }" :style="{ width: barPct + '%' }"></div></div>
          </template>
        </div>

        <!-- Current POP score -->
        <div class="publish-side-card">
          <div class="side-card-head"><h4>Current POP score</h4></div>
          <div class="score-big">
            <div class="score-big-num" :class="popColorClass">{{ popScore != null ? popScore : '-' }}<span class="denom">/100</span></div>
            <div class="score-big-lbl">Final</div>
          </div>
        </div>

        <!-- Word count -->
        <div class="publish-side-card">
          <div class="side-card-head"><h4>Word count</h4></div>
          <div class="wc-row">
            <div><span class="wc-num">{{ fmt(currentWords) }}</span><span class="wc-total"> / {{ fmt(targetWords) }}</span></div>
            <div class="wc-helper">{{ wcCompletePct }}% complete</div>
          </div>
          <div class="wc-bar"><div :style="{ width: wcPct + '%' }"></div></div>
        </div>

        <!-- Content readiness + term-coverage boxes (reused from the Draft/SEO
             sidebar; metric cards hidden since this aside shows its own). -->
        <report-sidebar
          only-terms
          step="publish"
          :session="session"
          :setup="(session && session.setup) || {}"
          :keyword="keyword"
          :planning="session && session.planning"
          :seo-quality="seoQuality"
        />

        <div class="publish-side-card">
          <div class="dl-wrap">
            <button class="pub-btn btn-primary block" @click="dlMenu = !dlMenu"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> Download article</button>
            <div v-if="dlMenu" class="dl-menu">
              <button v-for="f in FORMATS" :key="f.value" :disabled="exporting" @click="download(f.value)">{{ f.label }}</button>
            </div>
          </div>
          <p class="note">Choose any format. POP doesn't publish to a CMS - download and add it to your site yourself.</p>
        </div>

        <div class="publish-side-card">
          <h5>Metadata</h5>
          <div class="publish-meta-field">
            <label>SEO title <button class="meta-copy" title="Copy" @click="copy(meta.seo_title)"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg></button></label>
            <input v-model="meta.seo_title" @blur="saveMeta" />
          </div>
          <div class="publish-meta-field">
            <label>Slug <button class="meta-copy" title="Copy" @click="copy(meta.slug)"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg></button></label>
            <input v-model="meta.slug" @blur="saveMeta" />
          </div>
          <div class="publish-meta-field">
            <label>Meta description <button class="meta-copy" title="Copy" @click="copy(meta.meta_description)"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg></button></label>
            <textarea rows="3" v-model="meta.meta_description" @blur="saveMeta"></textarea>
          </div>
        </div>

      </aside>

    <!-- Start over confirmation (replaces the browser confirm) -->
    <div v-if="startOverModal.show" class="aiwm-overlay" @click.self="closeStartOver">
      <div class="aiwm-modal" role="dialog" aria-modal="true" style="overflow: initial !important;">
        <div class="aiwm-head">
          <h3>Starting over will erase your article</h3>
          <button class="aiwm-close" aria-label="Close" @click="closeStartOver">×</button>
        </div>
        <div class="aiwm-body">
          <p>Your setup, titles, structure and finished draft are all deleted, and the AI writer resets to the first step. This can't be undone.</p>
          <div class="aiwm-cost"><span>⚡</span> We strongly recommend downloading your article first</div>
        </div>
        <div class="aiwm-foot">
          <button class="aiwm-btn aiwm-soft" @click="closeStartOver">Cancel</button>
          <div class="dl-wrap so-dl">
            <button class="aiwm-btn aiwm-soft" @click="startOverDownload">Download first</button>
            <div v-if="startOverDl" class="dl-menu dl-up">
              <button v-for="f in FORMATS" :key="f.value" :disabled="exporting" @click="download(f.value)">{{ f.label }}</button>
            </div>
          </div>
          <button class="aiwm-btn aiwm-danger" @click="confirmStartOver">Yes, start over</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Hero from "../../components/Hero.vue";
import ReportSidebar from "../../sidebar/ReportSidebar.vue";
import { beautifyHtmlString } from "@/components/FullPageSourceEditorV2/components/AiWriter/helper";
import { saveAs } from "file-saver";
import { runExport, highlightHtml, withPageTitle } from "./exporters";
import { celebrate } from "../../utils/confetti";
import {
  aiWriterGetBlocks, aiWriterMetadataPatch,
  aiWriterStartOver,
} from "@/api";

const TABS = ["Desktop", "Mobile", "HTML"];
const FORMATS = [
  { value: "plain_txt", label: "plain .txt" },
  { value: "html_txt", label: "html .txt" },
  { value: "html", label: ".html" },
  { value: "zip", label: ".zip (.html + images)" },
  { value: "docx", label: ".docx" },
  { value: "docx_highlighted", label: ".docx with highlighted keywords" },
  { value: "outline", label: "Outline only .html" },
];

export default {
  name: "AiwV2PublishStep",
  components: { Hero, ReportSidebar },
  props: {
    aiRunId: { default: null },
    keyword: { type: String, default: "" },
    mode: { type: String, default: "" },
    pageRun: { type: Object, default: () => ({}) },
    session: { type: Object, default: () => ({}) },
    seoQuality: { type: Object, default: null },
  },
  data() {
    const publish = (this.session && this.session.publish) || {};
    return {
      TABS, FORMATS,
      tab: "Desktop",
      highlight: false,
      loading: true,
      exporting: false,
      dlMenu: false,
      startOverModal: { show: false },
      startOverDl: false,
      fullHtml: "",
      meta: { ...(publish.metadata || { seo_title: "", slug: "", meta_description: "" }) },
      showRegenExplain: false,
      rcMenuOpen: false,
      // Floating per-image download button (positioned over the hovered image).
      imgDl: { show: false, top: 0, left: 0, src: "" },
    };
  },
  computed: {
    token() {
      return this.$store.getters.getToken;
    },
    terms() {
      const tp = (this.session && this.session.planning && this.session.planning.term_plan) || {};
      const out = [];
      ["title", "pageTitle", "subHeadings", "p"].forEach((s) => {
        (tp[s] || []).forEach((t) => { if (t && t.term) out.push(t.term); });
      });
      return out;
    },
    rows() {
      return (this.session && this.session.structure && this.session.structure.rows) || [];
    },
    baseName() {
      return "ai-article-" + (this.aiRunId || "");
    },
    articleTitle() {
      const t = (this.session && this.session.title && this.session.title.selected) || {};
      return t.page_h1 || t.seo_title || this.meta.seo_title || this.keyword || "";
    },
    previewUrl() {
      let host = "your-site.com";
      const u = (this.pageRun && this.pageRun.url) || (this.page && this.page.url) || "";
      if (u) {
        try { host = new URL(u.indexOf("http") === 0 ? u : "https://" + u).host; } catch (e) { /* keep default */ }
      }
      const slug = this.meta.slug || "your-article";
      return host + "/" + slug;
    },
    bodyHtml() {
      const doc = new DOMParser().parseFromString(this.fullHtml || "", "text/html");
      const body = doc.querySelector("body");
      return body ? body.innerHTML : this.fullHtml;
    },
    renderedBody() {
      if (this.highlight) {
        return highlightHtml(this.bodyHtml, this.terms, (m) => `<mark class="kw-hl">${m}</mark>`);
      }
      return this.bodyHtml;
    },
    beautified() {
      // The server fullHtml carries the <title> but no page <h1> (the preview
      // renders it separately) — inject it so the HTML tab / Copy HTML match the
      // downloads and include the page title.
      return beautifyHtmlString(withPageTitle(this.fullHtml || "", this.articleTitle));
    },
    // --- sidebar metric cards (same data + logic as ReportSidebar) ---
    unlimitedRegen() {
      return !!(this.session && this.session.credits && this.session.credits.unlimitedRegenerate);
    },
    // Own-key runs consume the free pool first, then regenerates stay free.
    ownKeyFree() {
      return !!(this.session && this.session.credits && this.session.credits.ownKeyFree);
    },
    ownKeyProvider() {
      return (this.session && this.session.credits && this.session.credits.ownKeyProvider) || null;
    },
    ownKeyModelLabel() {
      return (this.session && this.session.credits && this.session.credits.ownKeyModelLabel) || '';
    },
    regenIncluded() {
      const c = this.session && this.session.credits;
      return c && c.regenerateIncluded != null ? c.regenerateIncluded : 5;
    },
    regenRemaining() {
      const c = this.session && this.session.credits;
      return c && c.regenerateRemaining != null ? c.regenerateRemaining : this.regenIncluded;
    },
    regenUsed() {
      return Math.max(0, this.regenIncluded - this.regenRemaining);
    },
    regenPct() {
      return this.regenIncluded ? (this.regenUsed / this.regenIncluded) * 100 : 0;
    },
    // Per-category TOTAL regenerate-run counts (backend planning_dict.regenerate_counts).
    counts() {
      const c = (this.session && this.session.credits && this.session.credits.regenerateCounts) || {};
      return { title: c.title || 0, heads: c.heads || 0, content: c.content || 0, rich: c.rich || 0 };
    },
    paid() {
      return (this.session && this.session.credits && this.session.credits.regeneratePaid) || 0;
    },
    freeUsed() {
      return Math.min(5, this.regenUsed);
    },
    barPct() {
      return Math.min(100, ((this.freeUsed + this.paid) / 5) * 100);
    },
    popScore() {
      const s = this.session && this.session.scores;
      return s && s.popScore != null ? Math.round(s.popScore) : null;
    },
    popColorClass() {
      const v = this.popScore;
      if (v == null) return "";
      if (v >= 85) return "tone-green";
      return v >= 60 ? "tone-amber" : "tone-red";
    },
    currentWords() {
      const s = this.session || {};
      return (s.scores && s.scores.wordCount) || (s.blocksSummary && s.blocksSummary.wordCount) || 0;
    },
    targetWords() {
      const s = this.session || {};
      return (s.scores && s.scores.targetWordCount) || 0;
    },
    wcPct() {
      if (!this.targetWords) return 0;
      return Math.min(100, (this.currentWords / this.targetWords) * 100);
    },
    wcCompletePct() {
      return Math.round(this.wcPct);
    },
    // Page experience lives on the seoQuality object (same source as ReportSidebar);
    // session.scores does not carry it.
    pageExp() {
      const v = this.seoQuality && this.seoQuality.scores && this.seoQuality.scores.page_experience_score;
      return v != null ? v : null;
    },
    // Hero meta tail: "N words · POP score X · Page experience Y". The score
    // fragments are omitted when unavailable (e.g. a Fast-Draft run that skipped
    // the full SEO analysis) rather than rendering "POP score null".
    metaItems() {
      let s = `${this.fmt(this.currentWords)} words`;
      if (this.popScore != null) s += ` · POP score ${this.popScore}`;
      if (this.pageExp != null) s += ` · Page experience ${this.pageExp}`;
      return [s];
    },
  },
  watch: {
    // The button anchors to a rendered <img>; any rerender or tab switch
    // invalidates the position, so hide until the next hover.
    tab() {
      this.imgDl.show = false;
    },
    renderedBody() {
      this.imgDl.show = false;
    },
  },
  async mounted() {
    await this.load();
  },
  methods: {
    fmt(n) {
      return (n || 0).toLocaleString("en-US");
    },
    onArtMouseOver(e) {
      const t = e.target;
      if (t && t.tagName === "IMG" && (t.getAttribute("src") || "").includes("/ai-run/images/")) {
        const art = e.currentTarget;
        const artRect = art.getBoundingClientRect();
        const imgRect = t.getBoundingClientRect();
        this.imgDl = {
          show: true,
          top: imgRect.top - artRect.top + art.scrollTop + 10,
          left: imgRect.right - artRect.left - 44,
          src: t.currentSrc || t.src,
        };
        return;
      }
      // Keep the button while hovering it; hide on anything else.
      if (!(t && t.closest && t.closest(".img-dl-btn"))) this.imgDl.show = false;
    },
    async downloadHoverImage() {
      const src = this.imgDl.src;
      if (!src) return;
      const filename = (src.split("/").pop() || "image.png").split("?")[0];
      try {
        const res = await fetch(src);
        if (!res.ok) throw new Error("HTTP " + res.status);
        saveAs(await res.blob(), filename);
      } catch (e) {
        // Fallback: open the image directly so the user can still save it.
        window.open(src, "_blank");
      }
    },
    async load() {
      this.loading = true;
      try {
        const blocksRes = await aiWriterGetBlocks(this.token, this.aiRunId);
        this.fullHtml = blocksRes?.data?.fullHtml || "";
      } finally {
        this.loading = false;
      }
    },
    copyHtml() {
      if (navigator.clipboard) navigator.clipboard.writeText(this.beautified);
    },
    copy(text) {
      if (navigator.clipboard) navigator.clipboard.writeText(text || "");
    },
    async download(format) {
      this.dlMenu = false;
      this.startOverDl = false;
      this.exporting = true;
      try {
        await runExport(format, { token: this.token, fullHtml: this.fullHtml, terms: this.terms, rows: this.rows, baseName: this.baseName, pageTitle: this.articleTitle, seoTitle: this.meta.seo_title });
        celebrate();
      } catch (e) {
        this.$store.commit("setGlobalErrorMsg", { msg: "Download failed." });
      } finally {
        this.exporting = false;
      }
    },
    async saveMeta() {
      try {
        await aiWriterMetadataPatch(this.token, this.aiRunId, {
          seoTitle: this.meta.seo_title, slug: this.meta.slug, metaDescription: this.meta.meta_description,
        });
      } catch (e) { /* ignore */ }
    },
    startOver() {
      this.startOverDl = false;
      this.startOverModal = { show: true };
    },
    closeStartOver() {
      this.startOverModal.show = false;
      this.startOverDl = false;
    },
    startOverDownload() {
      this.startOverDl = !this.startOverDl;
    },
    async confirmStartOver() {
      this.startOverModal.show = false;
      this.startOverDl = false;
      try {
        const res = await aiWriterStartOver(this.token, this.aiRunId, false);
        if (res?.data?.newAiRunId) this.$emit("switch", { id: res.data.newAiRunId });
      } catch (e) { /* ignore */ }
    },
  },
};
</script>

<style scoped>
/* Page-level layout (matches the design): the main column (hero + status +
   preview) with the publish side panel beside it as the right column. */
.aiw2-publish { display: grid; grid-template-columns: minmax(0, 1fr) 300px; gap: 24px; align-items: stretch; }
.publish-main { min-width: 0; display: flex; flex-direction: column; }

/* Status banner */

/* Keep the side panel as a sidebar across desktop/tablet widths; only stack it
   under the main column on genuinely narrow (phone) viewports. */
@media (max-width: 640px) { .aiw2-publish { grid-template-columns: 1fr; } }

/* Article preview shell (browser chrome) */
.article-preview { background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); overflow: hidden; display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; }
.article-preview-toolbar { display: flex; align-items: center; gap: 10px; padding: 10px 16px; background: var(--surface-2, #f7f7f7); border-bottom: 1px solid var(--border, #e4e4e4); }
.article-preview-toolbar .dots { display: flex; gap: 5px; }
.article-preview-toolbar .dots span { width: 9px; height: 9px; border-radius: 50%; background: var(--border-strong, #ccc); }
.article-preview-toolbar .url-bar { flex: 1; background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: 5px; padding: 4px 10px; font-size: 11.5px; color: var(--text-3, #6b7b7e); font-family: ui-monospace, SFMono-Regular, Menlo, monospace; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.article-preview-toolbar .url-bar .lock { color: var(--pop-green, #25a97a); margin-right: 5px; }
.article-preview-toolbar .preview-tabs { display: flex; background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: 5px; padding: 2px; gap: 1px; }
.preview-tab-btn { background: transparent; border: none; padding: 4px 10px; font-family: inherit; font-size: 11px; font-weight: 600; color: var(--text-3, #6b7b7e); cursor: pointer; border-radius: 3px; }
.preview-tab-btn.active { background: var(--pop-ink, #012323); color: #fff; }

/* Controls bar */
.preview-controls-bar { display: flex; align-items: center; gap: 12px; padding: 10px 16px; background: var(--surface-2, #f7f7f7); border-bottom: 1px solid var(--border, #e4e4e4); }
.kw-toggle { display: inline-flex; align-items: center; gap: 9px; font-size: 13px; font-weight: 600; color: var(--text-2, #014751); cursor: pointer; margin-right: auto; }
.kw-toggle .sw { width: 38px; height: 22px; border-radius: 999px; position: relative; flex: none; transition: background .15s; background: #b5b5b5; }
.kw-toggle .sw .knob { position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; border-radius: 50%; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,.3); transition: left .15s; }
.kw-toggle .sw.on { background: #25a97a; }
.kw-toggle .sw.on .knob { left: 18px; }
.loading { padding: 40px; text-align: center; color: var(--text-3, #6b7b7e); }

/* HTML view */
.article-html-wrap { position: relative; }
.html-copy-btn { position: absolute; top: 12px; right: 16px; z-index: 2; display: inline-flex; align-items: center; gap: 6px; background: rgba(255,255,255,.12); color: #fff; border: 1px solid rgba(255,255,255,.25); font-family: inherit; font-size: 12px; font-weight: 700; padding: 6px 12px; border-radius: 7px; cursor: pointer; transition: background .12s; }
.html-copy-btn:hover { background: rgba(255,255,255,.22); }
.html-copy-btn svg { width: 14px; height: 14px; }
.article-html-view { margin: 0; padding: 18px 20px; background: #0f1f24; color: #cfe5ea; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; line-height: 1.6; white-space: pre-wrap; word-break: break-word; }

/* Article body — fills the remaining height of the preview box (which stretches
   to the sidebar) and scrolls internally, so a long article never overflows the
   box or gets clipped, and the box bottom stays aligned with the sticky sidebar. */
.article-body { padding: 36px 44px 40px; color: #222; line-height: 1.85; flex: 1 1 auto; min-height: 0; overflow-y: auto; position: relative; }
/* Floating per-image download button (anchored over the hovered image). */
.img-dl-btn { position: absolute; z-index: 3; width: 34px; height: 34px; display: inline-flex; align-items: center; justify-content: center; background: #fff; border: 1px solid var(--border-strong, #ccc); border-radius: 50%; color: var(--text-2, #014751); cursor: pointer; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18); transition: background .12s; }
.img-dl-btn:hover { background: var(--surface-2, #f7f7f7); }
.img-dl-btn svg { width: 15px; height: 15px; }
.article-body.mobile { max-width: 390px; margin: 0 auto; border-left: 1px solid var(--border, #e4e4e4); border-right: 1px solid var(--border, #e4e4e4); }
@media (max-width: 600px) { .article-body { padding: 24px 22px 28px; } }
.art-h1 { font-size: 30px; font-weight: 700; line-height: 1.18; margin: 0 0 16px; color: #111; letter-spacing: -.01em; }
.article-body :deep(h1) { font-size: 30px; font-weight: 700; line-height: 1.18; margin: 0 0 16px; color: #111; letter-spacing: -.01em; }
.article-body :deep(h2) { font-size: 22px; font-weight: 700; margin: 32px 0 12px; color: #111; line-height: 1.25; letter-spacing: -.005em; }
.article-body :deep(h3) { font-size: 17.5px; font-weight: 700; margin: 22px 0 8px; color: #222; }
.article-body :deep(p) { margin: 0 0 16px; font-size: 15.5px; color: #333; line-height: 1.85; }
.article-body :deep(a), .article-body :deep(.internal-link) { color: var(--pop-blue, #316bff); text-decoration: underline; text-decoration-color: rgba(0,117,255,.4); font-weight: 600; font-size: inherit; line-height: inherit; }
.article-body :deep(a:hover) { text-decoration-color: var(--pop-blue, #316bff); }
.article-body :deep(ul), .article-body :deep(ol) { margin: 16px 0; padding-left: 24px; font-size: 15px; color: #333; line-height: 1.75; }
.article-body :deep(li) { margin-bottom: 8px; }
.article-body :deep(table) { width: 100%; border-collapse: collapse; margin: 18px 0 22px; font-size: 14px; border: 1px solid #e5e1d7; border-radius: 6px; overflow: hidden; }
.article-body :deep(thead th) { background: #f5efe4; padding: 10px 12px; text-align: left; font-weight: 700; color: #333; font-size: 12.5px; border-bottom: 1px solid #e5e1d7; }
.article-body :deep(tbody td) { padding: 10px 12px; border-bottom: 1px solid #efeae0; color: #444; }
.article-body :deep(tbody tr:nth-child(even)) { background: #fbf9f5; }
.article-body :deep(nav.toc) { background: #fafaf7; border: 1px solid #e8e4d8; border-radius: 8px; padding: 14px 18px; margin: 0 0 22px; font-size: 13.5px; }
.article-body :deep(nav.toc ul) { margin: 0; padding-left: 18px; }
/* PM-spec TOC (nav.pop-toc — design .toc-render translated) */
.article-body :deep(nav.pop-toc) { border: 1px solid var(--border, #e4e4e4); border-radius: 8px; background: var(--surface-2, #f7f7f7); padding: 16px 18px; margin: 0 0 22px; }
.article-body :deep(nav.pop-toc h2) { font-size: 14px; font-weight: 800; color: var(--text-2, #014751); margin: 0 0 12px; text-transform: uppercase; letter-spacing: .05em; }
.article-body :deep(nav.pop-toc .toc-list) { list-style: none; margin: 0; padding: 0; counter-reset: toc; }
.article-body :deep(nav.pop-toc .toc-list > li) { counter-increment: toc; margin: 0 0 9px; }
.article-body :deep(nav.pop-toc .toc-list > li:last-child) { margin-bottom: 0; }
.article-body :deep(nav.pop-toc .toc-list a) { display: inline-flex; gap: 10px; color: var(--text-2, #014751); font-weight: 600; font-size: 14px; text-decoration: none; }
.article-body :deep(nav.pop-toc ol.toc-list > li > a)::before { content: counter(toc) "."; color: var(--pop-orange, #ff5700); font-weight: 800; min-width: 20px; }
.article-body :deep(nav.pop-toc ul.toc-list > li > a)::before { content: "•"; color: var(--pop-orange, #ff5700); font-weight: 800; min-width: 14px; }
.article-body :deep(nav.pop-toc ul.toc-plain > li > a)::before { content: none; }
.article-body :deep(nav.pop-toc .toc-list a:hover) { color: var(--pop-orange, #ff5700); }
.article-body :deep(nav.pop-toc .toc-sub) { list-style: none; margin: 7px 0 2px 30px; padding: 0; }
.article-body :deep(nav.pop-toc .toc-sub > li) { margin: 0 0 6px; }
.article-body :deep(nav.pop-toc .toc-sub a) { color: var(--text-3, #6b7b7e); font-weight: 500; font-size: 13px; text-decoration: none; display: inline-flex; gap: 8px; }
.article-body :deep(nav.pop-toc .toc-sub a)::before { content: "\2013"; color: #9aa6a8; min-width: 14px; }
/* Mobile preview: collapsed by default (list hidden, label row with chevron). */
.article-body.mobile :deep(nav.pop-toc .toc-list) { display: none; }
.article-body.mobile :deep(nav.pop-toc h2) { margin: 0; display: flex; align-items: center; justify-content: space-between; }
.article-body.mobile :deep(nav.pop-toc h2)::after { content: "▾"; color: var(--text-3, #6b7b7e); font-size: 12px; }
.article-body :deep(mark.kw-hl) { background: #fff24d; color: inherit; font-size: inherit !important; font-weight: inherit; font-family: inherit; line-height: inherit; border-radius: 2px; padding: 0 1px; }
.article-body :deep(img) { max-width: 100%; border-radius: 8px; margin: 18px 0; display: block; }

/* ===== Design-handoff article elements (Key Takeaways / List / FAQ / References).
   Each selector carries the block class so it out-specifies the generic ul/ol/li/p/h2
   rules above, and restores list markers the app's global reset strips. ===== */
/* Key takeaways callout (design .article-takeaways) */
.article-body :deep(.article-takeaways) { background: linear-gradient(135deg,#FFF7DB 0%,#FDEFC9 55%,#FCE7D6 100%); border: 1px solid rgba(217,178,90,.35); border-radius: 14px; padding: 22px 24px; margin: 24px 0 28px; box-shadow: 0 4px 18px rgba(217,178,90,.14); }
.article-body :deep(.article-takeaways h4) { font-size: 11.5px; font-weight: 800; text-transform: uppercase; letter-spacing: .08em; color: var(--pop-orange-deep,#b23d00); margin: 0 0 14px; display: flex; align-items: center; gap: 7px; }
.article-body :deep(.article-takeaways ul) { margin: 0; padding: 0; list-style: none; font-size: 14.5px; color: #4a3d28; line-height: 1.55; }
.article-body :deep(.article-takeaways li) { position: relative; margin-bottom: 11px; padding-left: 24px; }
.article-body :deep(.article-takeaways li)::before { content: ''; position: absolute; left: 2px; top: 8px; width: 7px; height: 7px; border-radius: 50%; background: linear-gradient(135deg,#F0A030,var(--pop-orange,#ff5700)); }
.article-body :deep(.article-takeaways li:last-child) { margin-bottom: 0; }

/* Boxed list (design .article-list) — its <h3> title sits above the box */
.article-body :deep(h4) { font-size: 15.5px; font-weight: 700; margin: 20px 0 6px; color: #222; }
.article-body :deep(h3 + .article-list), .article-body :deep(h4 + .article-list) { margin-top: 8px; }
.article-body :deep(.article-list) { background: #FAFAF7; border: 1px solid #E8E4D8; border-radius: 8px; padding: 16px 20px 16px 36px; margin: 18px 0; font-size: 14.5px; color: #333; list-style: disc outside; }
.article-body :deep(.article-list li) { display: list-item; margin-bottom: 8px; line-height: 1.55; }
.article-body :deep(.article-list li:last-child) { margin-bottom: 0; }

/* FAQ (design .article-faq-*), targeting the app's .faq-item markup; no "Q." prefix in publish */
.article-body :deep(.faq-item) { border-bottom: 1px solid #eee; padding: 14px 0; }
.article-body :deep(.faq-item:first-child) { border-top: 1px solid #eee; }
.article-body :deep(.faq-item .question h4) { font-size: 15px; font-weight: 700; color: #111; margin: 0 0 6px; }
.article-body :deep(.faq-item .answer p) { font-size: 14px; color: #555; line-height: 1.6; margin: 0; }

/* Form placeholder (design .article-form-ph — dashed box, real embed added after publish) */
.article-body :deep(.article-form-ph) { display: flex; align-items: center; gap: 10px; border: 1px dashed var(--border-strong, #ccc); border-radius: 10px; padding: 16px 18px; margin: 24px 0; color: var(--text-3, #6b7b7e); font-size: 13px; line-height: 1.5; background: #fff; clear: both; box-sizing: border-box; }
.article-body :deep(.article-form-ph svg) { width: 24px; height: 24px; flex: none; }

/* References (design .article-sources) */
.article-body :deep(.article-sources) { margin: 28px 0 0; }
.article-body :deep(.article-sources h2) { margin-bottom: 6px; }
.article-body :deep(.article-sources .src-note) { font-size: 13px; color: #6b7a7d; margin-bottom: 10px; }
.article-body :deep(.article-sources ol), .article-body :deep(.article-sources ul) { margin: 0; padding-left: 22px; font-size: 14px; line-height: 1.7; color: #444; list-style: decimal outside; }
.article-body :deep(.article-sources li) { display: list-item; margin-bottom: 4px; }
.article-body :deep(.article-sources a) { color: var(--pop-blue,#316bff); text-decoration: underline; }

/* Publish side */
.publish-side { display: flex; flex-direction: column; gap: 12px; position: sticky; top: 18px; }
.publish-side-card { background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); padding: 16px 18px; }
/* Own-key "key is on" card (same look as the report sidebar's — mockup .api-key-card). */
.api-key-card { display: flex; align-items: stretch; gap: 0; padding: 0; overflow: hidden; }
.akc-ico { flex: none; width: 58px; display: flex; align-items: center; justify-content: center; }
.akc-body { padding: 12px 14px; }
.akc-title { font-size: 14px; font-weight: 800; color: var(--text, #052e33); }
.akc-sub { font-size: 12px; color: var(--text-2, #47606a); margin-top: 2px; }
/* provider identity: the two keys must never look alike at a glance */
.akc-mark {
  width: 30px; height: 30px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 800; letter-spacing: .02em; color: #fff;
}
.api-key-card.prov-openai { border-color: #10a37f; background: #F1FBF7; }
.api-key-card.prov-openai .akc-ico { background: #DCF5EC; }
.api-key-card.prov-openai .akc-mark { background: #10a37f; }
.api-key-card.prov-anthropic { border-color: #D97757; background: #FDF5F1; }
.api-key-card.prov-anthropic .akc-ico { background: #F7E3D9; }
.api-key-card.prov-anthropic .akc-mark { background: #D97757; }
.akc-model {
  display: inline-block; margin-top: 5px; font-size: 11px; font-weight: 700;
  color: var(--text-2, #47606a); background: #fff;
  border: 1px solid var(--border, #e4e4e4); border-radius: 999px; padding: 2px 8px;
}
.publish-side-card h5 { font-size: 14px; font-weight: 700; color: var(--text, #052e33); margin: 0 0 12px; text-transform: none; }
.dl-wrap { position: relative; }
.dl-menu { position: absolute; top: calc(100% + 6px); left: 0; right: 0; z-index: 30; background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: 10px; box-shadow: var(--shadow-lg, 0 10px 36px rgba(15,20,25,.08)); padding: 6px; display: flex; flex-direction: column; }
.dl-menu button { text-align: left; padding: 9px 11px; border: none; background: transparent; cursor: pointer; font-size: 13px; color: var(--text-2, #014751); border-radius: 7px; }
.dl-menu button:hover { background: var(--surface-2, #f7f7f7); }
/* "Download first" dropdown inside the Start-over modal opens upward (button sits at the modal's bottom). */
.aiwm-foot .dl-wrap.so-dl { display: inline-flex; }
.dl-menu.dl-up { top: auto; bottom: calc(100% + 6px); min-width: 220px; }
.note { font-size: 11.5px; color: var(--text-3, #6b7b7e); margin: 6px 0 0; line-height: 1.5; text-align: center; }
.publish-meta-field { margin-bottom: 12px; }
.publish-meta-field:last-child { margin-bottom: 0; }
.publish-meta-field label { display: flex; align-items: center; gap: 6px; font-size: 11px; color: var(--text-3, #6b7b7e); font-weight: 600; margin-bottom: 4px; }
.publish-meta-field input, .publish-meta-field textarea { width: 100%; box-sizing: border-box; border: 1px solid var(--border-strong, #ccc); border-radius: 6px; padding: 7px 9px; font-size: 12.5px; font-family: inherit; color: var(--text, #052e33); }
.publish-meta-field textarea { resize: vertical; min-height: 60px; line-height: 1.5; }
.publish-meta-field input:focus, .publish-meta-field textarea:focus { outline: none; border-color: var(--pop-orange, #ff5700); box-shadow: 0 0 0 3px var(--pop-orange-soft, #ffeddd); }
.meta-copy { margin-left: auto; width: 22px; height: 22px; flex: none; padding: 0; border: 1px solid var(--border, #e4e4e4); border-radius: 5px; background: #fff; color: var(--text-3, #6b7b7e); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.meta-copy svg { width: 13px; height: 13px; }
.meta-copy:hover { background: var(--surface-2, #f7f7f7); color: var(--text-2, #014751); border-color: var(--border-strong, #ccc); }

/* Buttons */
.pub-btn { font-family: inherit; cursor: pointer; border-radius: 7px; font-weight: 600; font-size: 13px; padding: 9px 16px; display: inline-flex; align-items: center; justify-content: center; gap: 6px; transition: all .15s; border: none; }
.pub-btn.block { width: 100%; }
.btn-primary { background: var(--pop-orange, #ff5700); color: #fff; box-shadow: 0 2px 6px rgba(255,87,0,.25); }
.btn-primary:hover { background: var(--pop-orange-hover, #e64e00); }
.btn-soft { background: #fff; color: var(--text, #052e33); border: 1px solid var(--border-strong, #ccc); }
.btn-soft:hover { background: var(--surface-2, #f7f7f7); }
.btn-sm { padding: 6px 12px; font-size: 12px; }
.btn-xs { padding: 4px 9px; font-size: 11.5px; }
.spin { width: 16px; height: 16px; border: 2px solid var(--border, #e4e4e4); border-top-color: var(--pop-orange, #ff5700); border-radius: 50%; display: inline-block; animation: aiw2-pub-spin .7s linear infinite; }
@keyframes aiw2-pub-spin { to { transform: rotate(360deg); } }

/* Sidebar metric cards (ported from ReportSidebar to match design handoff) */
.side-card-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; position: relative; }
.side-card-head h4 { flex: 1; margin: 0; font-size: 14px; font-weight: 700; color: var(--text, #052e33); display: flex; align-items: center; gap: 6px; }
.side-card-head .rc-icon { display: inline-flex; align-items: center; color: var(--text-3, #6b7b7e); }
.more { background: var(--surface-2, #f7f7f7); border: none; width: 26px; height: 26px; border-radius: 50%; color: var(--text-3, #6b7b7e); cursor: pointer; font-size: 14px; line-height: 1; display: flex; align-items: center; justify-content: center; }
.more:hover { background: var(--border, #e4e4e4); color: var(--text-2, #014751); }
.rc-menu { display: none; position: absolute; top: 30px; right: 0; width: 296px; background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); box-shadow: 0 18px 44px rgba(15,20,25,.18); padding: 6px; z-index: 60; }
.rc-menu.open { display: block; }
.rc-menu-row { display: flex; gap: 12px; align-items: baseline; padding: 9px 10px; border-radius: 6px; font-size: 12.5px; color: var(--text-2, #014751); }
.rc-menu-row:hover { background: var(--surface-2, #f7f7f7); }
.rc-menu-row span { flex: 1; font-size: 12.5px; }
.rc-menu-row b { font-weight: 800; color: var(--text, #052e33); font-variant-numeric: tabular-nums; }
.credits-row .val.over { color: var(--pop-orange-deep, #b23d00); }
.credits-link { display: inline-block; font-size: 12px; color: var(--pop-blue, #316bff); cursor: pointer; margin-bottom: 8px; }
.credits-link:hover { text-decoration: underline; }
.credits-explain { font-size: 11.5px; color: var(--text-3, #6b7b7e); line-height: 1.5; margin: 0 0 8px; }
.credits-row { display: flex; justify-content: space-between; align-items: center; font-size: 12.5px; margin-bottom: 6px; color: var(--text-2, #014751); }
.credits-row .val { font-weight: 700; color: var(--text, #052e33); }
.credits-row .val.unlimited { color: var(--pop-green, #25a97a); }
.credits-bar { height: 4px; background: var(--pop-orange-soft, #ffeddd); border-radius: 999px; overflow: hidden; }
.credits-bar > div { height: 100%; background: var(--pop-orange, #ff5700); border-radius: 999px; }
.score-big { text-align: center; padding: 6px 0 4px; }
.score-big-num { font-size: 44px; font-weight: 700; color: var(--text-muted, #9aa6a8); line-height: 1; }
.score-big-num.tone-green { color: var(--pop-green, #25a97a); }
.score-big-num.tone-amber { color: var(--pop-amber, #aa993b); }
.score-big-num.tone-red { color: var(--pop-red, #e5484d); }
.score-big-num .denom { font-size: 18px; color: var(--text-3, #6b7b7e); font-weight: 500; }
.score-big-lbl { font-size: 11px; color: var(--text-3, #6b7b7e); margin-top: 4px; font-weight: 500; }
.wc-row { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
.wc-num { font-size: 22px; font-weight: 700; color: var(--text, #052e33); }
.wc-total { color: var(--text-3, #6b7b7e); font-size: 14px; font-weight: 500; }
.wc-helper { font-size: 11.5px; color: var(--text-3, #6b7b7e); margin-left: auto; text-align: right; line-height: 1.4; }
.wc-bar { height: 4px; background: #fce4d7; border-radius: 999px; overflow: hidden; }
.wc-bar > div { height: 100%; background: linear-gradient(90deg, var(--pop-orange, #ff5700) 0%, #ff9966 100%); border-radius: 999px; transition: width .6s; }
</style>
